import { z } from 'zod';
import {
  ChecklistTemplateSchema,
  rowsFrom,
  wouldOverflow,
  type ChecklistTemplate,
} from '../../domain/entities/checklist-template.ts';
import { MAX_CHECKLIST_ITEMS, type Todo } from '../../domain/entities/todo.ts';
import { newId } from '../../domain/id.ts';
import { expectUnchanged, updateWrite } from '../../ports/batch.ts';
import { NotFoundError } from '../../ports/repository.ts';
import type { ClockPort } from '../../ports/clock-port.ts';
import type { DataPort } from '../../ports/data-port.ts';

/**
 * Keeping a checklist rather than retyping it (P3-09b).
 *
 * Company-level, like the chart of accounts: "what we always check before
 * a tank goes in the ground" belongs to the company, not to one job. The
 * maintainer's UST work is the case for it — the same fifteen rows get
 * typed onto every removal.
 *
 * **Applying copies.** A to-do's checklist is one field on the to-do, and
 * a template that was referenced rather than copied would rewrite history
 * every time somebody edited it: a job run last March would silently
 * acquire this March's rows. So `applyChecklistTemplate` builds fresh rows
 * with fresh ids, and the to-do stops caring about the template.
 *
 * That is the same decision `scheduleTemplates` took, and for the same
 * reason.
 */
export class ChecklistTemplateError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ChecklistTemplateError';
  }
}

export interface ChecklistTemplateContext {
  readonly data: DataPort;
  readonly clock: ClockPort;
  readonly companyId: string;
  readonly uid: string;
}

export const SaveChecklistTemplateInputSchema = z.object({
  name: z.string().trim().min(1, 'A template needs a name').max(200),
  description: z.string().trim().max(1000).default(''),
  /**
   * Rows as plain strings, because that is what somebody types. Ids are
   * minted here: a caller that had to invent them would be inventing
   * identity for text it is about to hand over whole.
   */
  rows: z
    .array(z.string().trim().min(1, 'A checklist row needs text').max(500))
    .max(MAX_CHECKLIST_ITEMS, `A template holds at most ${MAX_CHECKLIST_ITEMS} rows`)
    .default([]),
});
export type SaveChecklistTemplateInput = z.input<typeof SaveChecklistTemplateInputSchema>;

const scope = (context: ChecklistTemplateContext) => ({ companyId: context.companyId });

const templates = (context: ChecklistTemplateContext) =>
  context.data.collection<ChecklistTemplate>(
    { companyId: context.companyId },
    'checklistTemplates',
  );

export async function listChecklistTemplates(
  context: ChecklistTemplateContext,
): Promise<ChecklistTemplate[]> {
  const page = await templates(context).list();
  // Active first, then by name. A library of twenty is read, not queried.
  return [...page.items].sort(
    (a, b) => Number(b.active) - Number(a.active) || a.name.localeCompare(b.name),
  );
}

function parse(
  input: SaveChecklistTemplateInput,
): z.infer<typeof SaveChecklistTemplateInputSchema> {
  const parsed = SaveChecklistTemplateInputSchema.safeParse(input);
  if (!parsed.success) throw new ChecklistTemplateError(parsed.error.issues[0]!.message);
  return parsed.data;
}

export async function createChecklistTemplate(
  context: ChecklistTemplateContext,
  input: SaveChecklistTemplateInput,
): Promise<ChecklistTemplate> {
  const values = parse(input);
  const at = context.clock.now().toISOString();
  const template = ChecklistTemplateSchema.parse({
    id: newId(),
    companyId: context.companyId,
    name: values.name,
    description: values.description,
    rows: values.rows.map((text) => ({ id: newId(), text })),
    active: true,
    createdAt: at,
    createdBy: context.uid,
    updatedAt: at,
    updatedBy: context.uid,
    version: 1,
  });
  await templates(context).create(template);
  return template;
}

/**
 * Takes the **loaded template**, not an id.
 *
 * The same shape as `updateJob`, and for the reason recorded there: a
 * read-compute-write that fetches the record itself guards nothing —
 * `expectUnchanged` then carries the version it has just read rather than
 * the version the form was built from, and two concurrent editors both
 * succeed with one silently overwritten.
 */
export async function updateChecklistTemplate(
  context: ChecklistTemplateContext,
  template: ChecklistTemplate,
  input: SaveChecklistTemplateInput,
): Promise<ChecklistTemplate> {
  const values = parse(input);
  const updated = ChecklistTemplateSchema.parse({
    ...template,
    name: values.name,
    description: values.description,
    /**
     * Rows are replaced wholesale with new ids.
     *
     * A template's row ids are not referenced by anything — applying
     * copies the text into fresh items — so preserving them would buy
     * nothing and make an edit look like a partial update of something
     * that is actually a list of words.
     */
    rows: values.rows.map((text) => ({ id: newId(), text })),
    updatedAt: context.clock.now().toISOString(),
    updatedBy: context.uid,
  });
  await context.data.commit([
    updateWrite<ChecklistTemplate>(
      scope(context),
      'checklistTemplates',
      updated.id,
      updated,
      expectUnchanged(template),
    ),
  ]);
  return updated;
}

/**
 * Retired, never deleted — like a cost code.
 *
 * A template somebody applied last month is part of how that job was run.
 * Deleting it would not change the to-do it produced, because the rows are
 * copied, but it would remove the only record of where those rows came
 * from.
 */
export async function retireChecklistTemplate(
  context: ChecklistTemplateContext,
  template: ChecklistTemplate,
): Promise<ChecklistTemplate> {
  const updated = {
    ...template,
    active: false,
    updatedAt: context.clock.now().toISOString(),
    updatedBy: context.uid,
  };
  await context.data.commit([
    updateWrite<ChecklistTemplate>(
      scope(context),
      'checklistTemplates',
      updated.id,
      updated,
      expectUnchanged(template),
    ),
  ]);
  return updated;
}

export async function reinstateChecklistTemplate(
  context: ChecklistTemplateContext,
  template: ChecklistTemplate,
): Promise<ChecklistTemplate> {
  const updated = {
    ...template,
    active: true,
    updatedAt: context.clock.now().toISOString(),
    updatedBy: context.uid,
  };
  await context.data.commit([
    updateWrite<ChecklistTemplate>(
      scope(context),
      'checklistTemplates',
      updated.id,
      updated,
      expectUnchanged(template),
    ),
  ]);
  return updated;
}

/**
 * Add a template's rows to a to-do's checklist.
 *
 * **Appended, not replaced.** Somebody applying a template to a to-do that
 * already has rows means "and also these"; replacing would delete work
 * that may already be ticked, which is not a thing a button labelled "add
 * a template" should be able to do.
 *
 * Refused rather than truncated when it would overflow. Silently dropping
 * the last three rows of a safety checklist is the worst available
 * outcome, and the cap is the same on both sides precisely so this is
 * rare.
 */
export async function applyChecklistTemplate(
  context: ChecklistTemplateContext,
  jobId: string,
  todo: Todo,
  template: ChecklistTemplate,
): Promise<Todo> {
  if (template.rows.length === 0) {
    throw new ChecklistTemplateError(`${template.name} has no rows to add`);
  }
  if (wouldOverflow(todo.checklist.length, template)) {
    throw new ChecklistTemplateError(
      `That would take the checklist past ${MAX_CHECKLIST_ITEMS} rows. Remove some first.`,
    );
  }

  const updated: Todo = {
    ...todo,
    checklist: [...todo.checklist, ...rowsFrom(template, newId)],
    updatedAt: context.clock.now().toISOString(),
    updatedBy: context.uid,
  };
  await context.data.commit([
    updateWrite<Todo>(
      { companyId: context.companyId, jobId },
      'todos',
      todo.id,
      { checklist: updated.checklist, updatedAt: updated.updatedAt, updatedBy: updated.updatedBy },
      expectUnchanged(todo),
    ),
  ]);
  return updated;
}

/** One template by id, for a page that was linked to rather than browsed. */
export async function getChecklistTemplate(
  context: ChecklistTemplateContext,
  id: string,
): Promise<ChecklistTemplate> {
  const found = await templates(context).get(id);
  if (!found) throw new NotFoundError('checklistTemplates', id);
  return found;
}
